import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { RotateCcwIcon, CodeIcon, GitCompareIcon } from "lucide-react";
import type {
  RefactorContent,
  RefactorUIAnswer,
  RefactorHiddenAnswer,
  CodeSnippet,
} from "@/types/assessment";
import { getInitialLanguage } from "@/types/assessment";
import { FileTabs, LanguageSelector } from "../shared";
import { CodeEditor } from "./code-editor";
import { DiffViewer } from "./diff-viewer";

interface RefactorInputProps {
  content: RefactorContent;
  value: RefactorUIAnswer | null;
  onChange: (value: RefactorUIAnswer) => void;
  onLanguageSave?: (language: string) => void;
  disabled?: boolean;
  showResults?: boolean;
  hiddenAnswer?: RefactorHiddenAnswer;
  userDefaultLanguage?: string;
  vimEnabled?: boolean;
  onVimToggle?: (enabled: boolean) => void;
}

type ViewMode = "editor" | "diff";

export function RefactorInput({
  content,
  value,
  onChange,
  onLanguageSave,
  disabled = false,
  showResults = false,
  hiddenAnswer,
  userDefaultLanguage,
  vimEnabled = false,
  onVimToggle,
}: RefactorInputProps) {
  // Get available languages
  const languages = Object.keys(content.snippets);

  // Initialize state
  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    getInitialLanguage(languages, userDefaultLanguage)
  );
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("editor");

  // Get current snippets for selected language
  const originalSnippets = content.snippets[selectedLanguage] || [];

  // Initialize or get edited code from value
  const getEditedCode = useCallback((): CodeSnippet[] => {
    if (value?.language === selectedLanguage && value.code.length > 0) {
      return value.code;
    }
    // Initialize with original code
    return originalSnippets.map((s) => ({ ...s }));
  }, [value, selectedLanguage, originalSnippets]);

  const editedCode = getEditedCode();
  const currentOriginalFile = originalSnippets[selectedFileIndex];
  const currentEditedFile = editedCode[selectedFileIndex];

  // Handle language change
  const handleLanguageChange = useCallback(
    (lang: string) => {
      setSelectedLanguage(lang);
      setSelectedFileIndex(0);
      // Reset to original code for new language
      const newSnippets = content.snippets[lang] || [];
      onChange({
        language: lang,
        code: newSnippets.map((s) => ({ ...s })),
      });
      // Save the language preference to the backend
      onLanguageSave?.(lang);
    },
    [content.snippets, onChange, onLanguageSave]
  );

  // Handle code change
  const handleCodeChange = useCallback(
    (newCode: string) => {
      const newEditedCode = editedCode.map((file, idx) =>
        idx === selectedFileIndex ? { ...file, code: newCode } : file
      );
      onChange({
        language: selectedLanguage,
        code: newEditedCode,
      });
    },
    [editedCode, selectedFileIndex, selectedLanguage, onChange]
  );

  // Handle reset to original
  const handleReset = useCallback(() => {
    onChange({
      language: selectedLanguage,
      code: originalSnippets.map((s) => ({ ...s })),
    });
  }, [originalSnippets, selectedLanguage, onChange]);

  // Check if code has been modified
  const hasModifications =
    currentOriginalFile?.code !== currentEditedFile?.code;

  // Use ref to avoid onChange in effect dependencies (fix 2.7)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Initialize value on mount if not set
  useEffect(() => {
    if (!value && originalSnippets.length > 0) {
      onChangeRef.current({
        language: selectedLanguage,
        code: originalSnippets.map((s) => ({ ...s })),
      });
    }
  }, [value, originalSnippets, selectedLanguage]);

  return (
    <div className="space-y-4">
      {/* Language selector and vim toggle */}
      <div className="flex items-center justify-between">
        {languages.length > 1 ? (
          <LanguageSelector
            languages={languages}
            selected={selectedLanguage}
            onChange={handleLanguageChange}
            disabled={disabled}
          />
        ) : (
          <div /> // Spacer when no language selector
        )}

        {/* Vim toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="vim-mode"
            checked={vimEnabled}
            onCheckedChange={(checked) => onVimToggle?.(checked)}
            disabled={disabled}
          />
          <Label
            htmlFor="vim-mode"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            Vim
          </Label>
        </div>
      </div>

      {/* File tabs */}
      <FileTabs
        files={originalSnippets}
        selectedIndex={selectedFileIndex}
        onChange={setSelectedFileIndex}
        disabled={disabled}
      />

      {/* View mode toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
          <button
            onClick={() => setViewMode("editor")}
            disabled={disabled}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-all",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              viewMode === "editor"
                ? "bg-background text-foreground shadow-sm font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <CodeIcon className="size-4" />
            Editor
          </button>
          <button
            onClick={() => setViewMode("diff")}
            disabled={disabled}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-all",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              viewMode === "diff"
                ? "bg-background text-foreground shadow-sm font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <GitCompareIcon className="size-4" />
            Diff
          </button>
        </div>

        {/* Reset button */}
        {!disabled && hasModifications && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="text-muted-foreground"
          >
            <RotateCcwIcon className="size-4" />
            Reset to Original
          </Button>
        )}
      </div>

      {/* Editor or Diff view */}
      {viewMode === "editor" ? (
        <CodeEditor
          value={currentEditedFile?.code || ""}
          language={selectedLanguage}
          onChange={handleCodeChange}
          disabled={disabled}
          minHeight="400px"
          placeholder="Start typing your refactored code..."
          vimEnabled={vimEnabled}
        />
      ) : (
        <DiffViewer
          original={currentOriginalFile?.code || ""}
          modified={currentEditedFile?.code || ""}
          language={selectedLanguage}
        />
      )}

      {/* Results panel (after submission) */}
      {showResults && hiddenAnswer && (
        <RefactorResultsPanel
          hiddenAnswer={hiddenAnswer}
          selectedLanguage={selectedLanguage}
        />
      )}
    </div>
  );
}

// Results panel shown after submission
function RefactorResultsPanel({
  hiddenAnswer,
  selectedLanguage,
}: {
  hiddenAnswer: RefactorHiddenAnswer;
  selectedLanguage: string;
}) {
  const expectedCode = hiddenAnswer.expected_code[selectedLanguage];
  const criteria = hiddenAnswer.evaluation_criteria;

  return (
    <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
      <h4 className="font-medium">Evaluation Criteria</h4>
      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
        {criteria.map((criterion, idx) => (
          <li key={idx}>{criterion}</li>
        ))}
      </ul>

      {expectedCode && expectedCode.length > 0 && (
        <>
          <h4 className="font-medium pt-2">Expected Solution</h4>
          <div className="rounded-lg border overflow-hidden bg-[var(--code-bg)]">
            <pre className="p-4 overflow-x-auto font-mono text-sm">
              {expectedCode[0].code}
            </pre>
          </div>
          {hiddenAnswer.acceptable_variations && (
            <p className="text-sm text-muted-foreground italic">
              Note: {hiddenAnswer.acceptable_variations}
            </p>
          )}
        </>
      )}
    </div>
  );
}
